import { useEffect, useState } from 'react';
import type { IEpisodeCategory } from '../../../common/interfaces/episodeCategory';
import { fetchJson } from '../../store/fetch';
import { translateKey } from '../../translate';
import { SettingsCategoryFilterButtonRow } from './SettingsCategoryFilterButtonRow';

interface IEpisodeCategoryButtonRowProps {
    employmentId?: string;
    onCategoryClick: (category: IEpisodeCategory) => void;
    organizationId?: string;
    selectedCategoryId?: string;
}

export function EpisodeCategoryButtonRow(props: IEpisodeCategoryButtonRowProps) {
    const [episodeCategories, setEpisodeCategories] = useState<IEpisodeCategory[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<IEpisodeCategory>();

    useEffect(() => {
        loadEpisodeCategories();
    }, [props.employmentId, props.organizationId]);

    if (!props.employmentId && !props.organizationId) {
        return null;
    }

    if (props.employmentId && props.organizationId) {
        throw new Error('Expected either employmentId or organizationId prop, but both were provided.');
    }

    if (episodeCategories.length < 2) {
        return null;
    }

    return (
        <SettingsCategoryFilterButtonRow
            collection={episodeCategories.map((category) => ({
                displayText: translateKey(`episodeCategoryEnum.${category.name}`),
                ...category,
            }))}
            onClick={categoryClickedHandler}
            selectedItem={selectedCategory}
        />
    );

    function categoryClickedHandler(category: IEpisodeCategory) {
        setSelectedCategory(category);
        if (selectedCategory?.id !== category.id) {
            props.onCategoryClick(category);
        }
    }

    async function loadEpisodeCategories() {
        if (props.employmentId || props.organizationId) {
            const categories = props.employmentId
                ? await fetchEpisodeCategoriesByEmployment(props.employmentId)
                : await fetchEpisodeCategoriesByOrganization(props.organizationId!);

            setEpisodeCategories(categories);

            if (categories.length > 0) {
                const preselectedCategory = props.selectedCategoryId
                    ? categories.find((category) => category.id === props.selectedCategoryId)
                    : undefined;

                const initialSelected = preselectedCategory || categories[0];
                setSelectedCategory(initialSelected);
                props.onCategoryClick(initialSelected);
            }
        }
    }
}

export async function fetchEpisodeCategoriesByOrganization(organizationId: string) {
    const organization = await fetchJson(
        `/api/episodeCategories?organizationId=${organizationId}&isActive=true&isConsumer=true&isProvider=true&excludeServicesWithoutServiceGroup=true&excludeServiceTypeOther=true`,
        'GET'
    );

    return organization.json.items as IEpisodeCategory[];
}

export async function fetchEpisodeCategoriesByEmployment(employmentId: string) {
    const employment = await fetchJson(
        `/api/episodeCategories?employmentId=${employmentId}&isActive=true&isConsumer=true&isProvider=true&isSupervisor=true&excludeServicesWithoutServiceGroup=true&excludeServiceTypeOther=true`,
        'GET'
    );

    return employment.json.items as IEpisodeCategory[];
}

export async function hasOrganizationEpisodeCategories(organizationId: string) {
    return (await fetchEpisodeCategoriesByOrganization(organizationId)).length > 0;
}

export async function hasEmploymentEpisodeCategories(employmentId: string) {
    return (await fetchEpisodeCategoriesByEmployment(employmentId)).length > 0;
}
